import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp, SectionHeading, IllustrativeBadge, EASE } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";
import { GALLERY_IMAGES } from "../data/site";

export default function Gallery() {
  const { T } = useLang();
  const [selected, setSelected] = useState(null);

  const close = () => setSelected(null);
  const step = (dir) =>
    setSelected((s) => (s === null ? s : (s + dir + GALLERY_IMAGES.length) % GALLERY_IMAGES.length));

  useEffect(() => {
    if (selected === null) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [selected]);

  const label = (i) => T.gallery.labels[i] || "";

  return (
    <section id="galeria" data-testid="gallery-section" className="bg-federasol-blush/60 py-14 md:py-24">
      <div className="container-x">
        <SectionHeading
          eyebrow={T.gallery.eyebrow}
          title={T.gallery.title}
          subtitle={T.gallery.subtitle}
        />

        <div className="mt-12 columns-2 gap-3 [column-fill:balance] sm:gap-4 md:columns-3 md:mt-14">
          {GALLERY_IMAGES.map((item, i) => (
            <FadeUp key={`${label(i)}-${i}`} delay={(i % 3) * 0.08} className="mb-3 break-inside-avoid sm:mb-4">
              <figure
                data-testid={`gallery-item-${i}`}
                onClick={() => setSelected(i)}
                className="zoom-media group relative cursor-pointer overflow-hidden rounded-2xl shadow-[0_10px_30px_-12px_rgba(64,49,47,0.2)]"
              >
                <img
                  src={item.src}
                  alt={item.real ? label(i) : `${label(i)} · ${T.badge}`}
                  loading="lazy"
                  className={`w-full object-cover transition-transform ${i % 3 === 1 ? "aspect-[4/5]" : i % 3 === 2 ? "aspect-square" : "aspect-[4/3]"}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2b1e1c]/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <figcaption className="absolute bottom-3 left-3 translate-y-3 font-serif text-base italic text-federasol-cream opacity-0 transition-all duration-500 ease-soft group-hover:translate-y-0 group-hover:opacity-100 sm:bottom-4 sm:left-4 sm:text-xl">
                  {label(i)}
                </figcaption>
                {!item.real && <IllustrativeBadge />}
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            data-testid="lightbox-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#2b1e1c]/92 p-4 backdrop-blur-sm md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label={T.gallery.close}
              className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-federasol-cream/30 text-federasol-cream transition-colors hover:bg-federasol-cream/10"
            >
              <X className="h-5 w-5" />
            </button>

            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label={T.gallery.prev}
              className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-federasol-cream/30 text-federasol-cream transition-colors hover:bg-federasol-cream/10 md:left-8"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label={T.gallery.next}
              className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-federasol-cream/30 text-federasol-cream transition-colors hover:bg-federasol-cream/10 md:right-8"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <motion.figure
              key={selected}
              className="max-w-4xl"
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.55, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY_IMAGES[selected].src}
                alt={GALLERY_IMAGES[selected].real ? label(selected) : `${label(selected)} · ${T.badge}`}
                className="max-h-[78vh] w-auto rounded-2xl object-contain shadow-2xl"
              />
              <figcaption className="mt-4 flex items-center justify-between px-1">
                <span className="font-serif text-lg italic text-federasol-cream">
                  {label(selected)}
                </span>
                <span className="text-xs font-medium uppercase tracking-widest text-federasol-cream/70">
                  {T.gallery.counter(selected + 1, GALLERY_IMAGES.length)}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
